import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { numberWithCommas } from "../utils.js";
import { fetchStageInfo, fetchStages } from "../data/stages.js";
import {
  buildReuseChart,
  buildTimelineRowFromFlights,
  extractFlightDates,
  statusIsActive,
} from "../graphComponents/reuseTimeline.js";
import { FleetSnapshotStrip, ReuseTimelinePanel, TopFlyerLink } from "./fleetPanels.js";
import "./vehicleList.css";
import "./BoosterStats.css";

const TIMELINE_ROW_LIMIT = 10;

const PAGE_COPY = {
  kicker: "Falcon fleet",
  title: "Booster Stats",
  blurb: "Reuse timeline and fleet snapshot for Falcon 9 and Heavy boosters.",
  timelineLabel: "Booster reuse timeline",
  timelineTitle: "Flights since debut",
  vehicleNoun: "boosters",
  detailPath: "stage",
  boostersHref: "/falcon/boosters/",
  compareHref: "/falcon/compare/",
};

/**
 * Booster fleet panels (reuse timeline + snapshot).
 * Used as a tab on the Falcon Stats page.
 * When `embedded`, the page chrome is omitted (parent shell supplies the title/switcher).
 */
export const BoosterStatsPanel = ({ embedded = false }) => {
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const [reuseTimeline, setReuseTimeline] = useState([]);
  const [reuseLoading, setReuseLoading] = useState(false);
  const [fleetLoading, setFleetLoading] = useState(true);
  const [reuseView, setReuseView] = useState("relative");
  const timelineCacheKey = useRef("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setFleetLoading(true);
      try {
        const stageData = await fetchStages("", null, "falcon", "BOOSTER");
        const fleet = stageData.stages || [];
        if (cancelled) {
          return;
        }

        setFleetVehicles(fleet);

        [...fleet]
          .filter((vehicle) => Number(vehicle.num_launches) > 0)
          .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
          .slice(0, TIMELINE_ROW_LIMIT)
          .forEach((vehicle) => {
            fetchStageInfo(vehicle.id).catch(() => {});
          });
      } catch (error) {
        if (!cancelled) {
          setFleetVehicles([]);
        }
      } finally {
        if (!cancelled) {
          setFleetLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const timelineVehicles = useMemo(
    () =>
      [...fleetVehicles]
        .filter((vehicle) => Number(vehicle.num_launches) > 0)
        .sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))
        .slice(0, TIMELINE_ROW_LIMIT),
    [fleetVehicles]
  );

  useEffect(() => {
    if (!timelineVehicles.length) {
      setReuseTimeline([]);
      timelineCacheKey.current = "";
      return undefined;
    }

    const cacheKey = timelineVehicles.map((vehicle) => vehicle.id).join(",");
    if (cacheKey === timelineCacheKey.current) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setReuseLoading(true);
      try {
        const details = await Promise.all(
          timelineVehicles.map(async (vehicle) => {
            try {
              const detail = await fetchStageInfo(vehicle.id);
              const flights = extractFlightDates(detail.stage_and_recoveries);
              return buildTimelineRowFromFlights({
                id: vehicle.id,
                name: vehicle.name,
                active: statusIsActive(vehicle.status),
                flights,
                count: Number(vehicle.num_launches || flights.length),
              });
            } catch (error) {
              return null;
            }
          })
        );

        if (!cancelled) {
          timelineCacheKey.current = cacheKey;
          const byId = new Map(details.filter(Boolean).map((row) => [String(row.id), row]));
          setReuseTimeline(timelineVehicles.map((vehicle) => byId.get(String(vehicle.id))).filter(Boolean));
        }
      } catch (error) {
        if (!cancelled) {
          setReuseTimeline([]);
        }
      } finally {
        if (!cancelled) {
          setReuseLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [timelineVehicles]);

  const reuseChart = useMemo(() => buildReuseChart(reuseTimeline, reuseView), [reuseTimeline, reuseView]);

  const summaryStats = useMemo(() => {
    const activeCount = fleetVehicles.filter((vehicle) => statusIsActive(vehicle.status)).length;
    const totalFlights = fleetVehicles.reduce((sum, vehicle) => sum + Number(vehicle.num_launches || 0), 0);
    const topFlyer = [...fleetVehicles].sort((a, b) => Number(b.num_launches || 0) - Number(a.num_launches || 0))[0];

    return {
      total: fleetVehicles.length,
      active: activeCount,
      flights: totalFlights,
      topFlyer: topFlyer && Number(topFlyer.num_launches || 0) > 0 ? topFlyer : null,
    };
  }, [fleetVehicles]);

  return (
    <div className={`bs-panel-root ${embedded ? "is-embedded" : ""}`}>
      {!embedded ? (
        <section className="bs-panel-intro">
          <div>
            <p className="bt-section-label">{PAGE_COPY.kicker}</p>
            <h2 className="bs-panel-title">{PAGE_COPY.title}</h2>
            <p className="bs-hero-blurb">{PAGE_COPY.blurb}</p>
            <div className="bs-hero-links">
              <Link className="bs-text-link" to={PAGE_COPY.boostersHref}>
                Browse boosters
              </Link>
              <Link className="bs-text-link" to={PAGE_COPY.compareHref}>
                Compare boosters
              </Link>
            </div>
          </div>
          <div className="vl-summary-grid">
            <span>
              <small>Total</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.total)}</strong>
            </span>
            <span>
              <small>Active</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.active)}</strong>
            </span>
            <span>
              <small>Flights</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.flights)}</strong>
            </span>
            <span>
              <small>Top flyer</small>
              {fleetLoading ? (
                <strong>…</strong>
              ) : (
                <TopFlyerLink vehicle={summaryStats.topFlyer} detailPath={PAGE_COPY.detailPath} />
              )}
            </span>
          </div>
        </section>
      ) : (
        <section className="bs-embedded-summary" aria-label="Booster fleet summary">
          <div className="vl-summary-grid">
            <span>
              <small>Total</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.total)}</strong>
            </span>
            <span>
              <small>Active</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.active)}</strong>
            </span>
            <span>
              <small>Flights</small>
              <strong>{fleetLoading ? "…" : numberWithCommas(summaryStats.flights)}</strong>
            </span>
            <span>
              <small>Top flyer</small>
              {fleetLoading ? (
                <strong>…</strong>
              ) : (
                <TopFlyerLink vehicle={summaryStats.topFlyer} detailPath={PAGE_COPY.detailPath} />
              )}
            </span>
          </div>
          <div className="bs-hero-links">
            <Link className="bs-text-link" to={PAGE_COPY.boostersHref}>
              Browse boosters
            </Link>
            <Link className="bs-text-link" to={PAGE_COPY.compareHref}>
              Compare boosters
            </Link>
          </div>
        </section>
      )}

      <div className="bs-panels">
        {fleetLoading && !fleetVehicles.length ? (
          <div className="vl-reuse-loading">
            <span className="loading-indicator" />
            <p>Loading booster fleet…</p>
          </div>
        ) : (
          <>
            <ReuseTimelinePanel
              copy={PAGE_COPY}
              reuseChart={reuseChart}
              reuseView={reuseView}
              setReuseView={setReuseView}
              loading={reuseLoading && !reuseTimeline.length}
              detailPath={PAGE_COPY.detailPath}
            />
            <FleetSnapshotStrip vehicles={fleetVehicles} copy={PAGE_COPY} />
          </>
        )}
      </div>
    </div>
  );
};

export default BoosterStatsPanel;
